// The cruises SSO part is disabled due to the client's requirement

import dayjs from "dayjs";
import { useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";

import { getAgentById } from "../../../store/actions/sessionActions";
import { useTypedDispatch, useTypedSelector } from "../../../store/store";
import { generateJWT } from "../../../utils/sso/generateJWT";
import type { JWTPayload } from "../../../utils/sso/types";
import LoadingContainer from "../../containers/LoadingContainer";

type Provider = "cruises" | "hotels";

function SSOGateway() {
  const dispatch = useTypedDispatch();
  const { provider } = useParams<{ provider: string }>();

  const ranRef = useRef(false);

  const { agent, agentDetails } = useTypedSelector((state) => state.session);

  const {
    brand,
    sso_enabled_brands,
    sso_cruises_url,
    sso_hotels_url,
    sso_cruises_jwt_key,
    sso_hotels_jwt_key,
  } = useTypedSelector((state) => state.environment);

  const isSSOEnabledForBrand = useMemo(() => {
    if (!sso_enabled_brands) {
      return false;
    }

    return sso_enabled_brands.split(",").includes(brand);
  }, [brand, sso_enabled_brands]);

  const normalizedProvider: Provider | null = useMemo(() => {
    if (provider === "cruises" || provider === "hotels") {
      return provider;
    }

    return null;
  }, [provider]);

  const target = useMemo(() => {
    if (!normalizedProvider) {
      return null;
    }

    if (normalizedProvider === "cruises") {
      return { url: sso_cruises_url, key: sso_cruises_jwt_key };
    }

    return { url: sso_hotels_url, key: sso_hotels_jwt_key };
  }, [
    normalizedProvider,
    sso_cruises_url,
    sso_cruises_jwt_key,
    sso_hotels_url,
    sso_hotels_jwt_key,
  ]);

  useEffect(() => {
    if (agent) {
      dispatch(getAgentById(agent));
    }
  }, [agent, dispatch]);

  useEffect(() => {
    if (ranRef.current) {
      return;
    }

    if (!isSSOEnabledForBrand) {
      return;
    }

    if (!normalizedProvider) {
      return;
    }

    if (!target?.url || !target?.key) {
      return;
    }

    if (!agent || !agentDetails) {
      return;
    }

    ranRef.current = true;

    const now = dayjs().unix();
    const expiration = now + 3600;

    const payload: JWTPayload = {
      email: agentDetails.email,
      externalCustomerId: agent,
      firstName: agentDetails.first_name ?? "",
      lastName: agentDetails.last_name ?? "",
      agency: agentDetails.agency,
      iat: now,
      exp: expiration,
    };

    const jwt = generateJWT(payload, target.key);

    window.location.href = `${target.url}?jwt=${jwt}`;
  }, [isSSOEnabledForBrand, normalizedProvider, target, agent, agentDetails]);

  return <LoadingContainer isLoading />;
}

export default SSOGateway;
